import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Clock, Mail, ArrowRight, RotateCw } from "lucide-react";
import { useI18n } from "../../i18n";
import { Container, Section, Card } from "../../components/ui/Primitives";
import { Button } from "../../components/ui/Button";
import { useDemo } from "../../components/demo/DemoContext";
import { demoResult } from "../../lib/demoApi";

const VISUALS = {
  passed: { icon: CheckCircle2, color: "hsl(152 62% 42%)", tone: "text-success" },
  failed: { icon: XCircle, color: "hsl(var(--destructive))", tone: "text-destructive" },
  expired: { icon: Clock, color: "hsl(var(--warning))", tone: "text-warning" },
  unknown: { icon: Clock, color: "hsl(var(--muted-foreground))", tone: "text-muted-foreground" },
};

export default function DemoResult() {
  const { t } = useI18n();
  const { openDemo } = useDemo();
  const [params] = useSearchParams();
  const status = (params.get("status") || "unknown").toLowerCase();
  const ref = params.get("ref") || "";
  const key = VISUALS[status] ? status : "unknown";
  const V = VISUALS[key];

  const [reportSent, setReportSent] = useState(false);
  const [email, setEmail] = useState("");

  // Poll the demo backend so the page can confirm when the full report is sent.
  useEffect(() => {
    if (!ref) return;
    let alive = true;
    const tick = async () => {
      try {
        const r = await demoResult(ref);
        if (!alive) return;
        setEmail(r.email || "");
        if (r.report_sent) {
          setReportSent(true);
          return; // stop polling
        }
      } catch {
        /* ignore */
      }
      if (alive) window.setTimeout(tick, 5000);
    };
    tick();
    return () => {
      alive = false;
    };
  }, [ref]);

  const title = t(`demo.result.title.${key}`);
  const lead = t(`demo.result.lead.${key}`);

  return (
    <section className="relative overflow-hidden border-b border-border bg-neutral-950 text-white">
      <div className="absolute inset-0 cyber-grid opacity-70" />
      <div
        className="pointer-events-none absolute -top-24 left-1/2 h-96 w-96 -translate-x-1/2 rounded-full blur-3xl"
        style={{ background: `${V.color}28` }}
      />
      <Container className="relative">
        <Section>
          <div className="mx-auto max-w-xl text-center">
            <span
              className="mx-auto flex h-20 w-20 items-center justify-center rounded-3xl"
              style={{ background: `${V.color}22`, color: V.color }}
            >
              <V.icon className="h-10 w-10" />
            </span>
            <h1 className="mt-6 text-3xl font-black text-white sm:text-4xl">{title}</h1>
            <p className="mt-4 text-lg leading-relaxed text-white/70">{lead}</p>

            <Card className="mx-auto mt-8 flex items-start gap-3 border-white/10 bg-white/[0.04] p-5 text-left">
              <Mail className="mt-0.5 h-5 w-5 shrink-0 text-brand" />
              <p className="text-sm text-white/80">
                {reportSent
                  ? t("demo.result.emailSent", null, { email })
                  : t("demo.result.email")}
              </p>
            </Card>

            <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
              <Button to="/" variant="light">
                {t("demo.result.back")} <ArrowRight className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                className="border-white/25 text-white hover:bg-white/10"
                onClick={openDemo}
              >
                <RotateCw className="h-4 w-4" /> {t("demo.result.again")}
              </Button>
            </div>

            {/* This demo is liveness-only — point to sales for the full platform */}
            <Card className="mx-auto mt-10 border-white/10 bg-white/[0.04] p-6 text-left">
              <h2 className="text-lg font-bold text-white">{t("demo.result.platformTitle")}</h2>
              <p className="mt-2 text-sm leading-relaxed text-white/70">{t("demo.result.platformText")}</p>
              <Button to="/contato" className="mt-5">
                {t("demo.result.platformCta")} <ArrowRight className="h-4 w-4" />
              </Button>
            </Card>
          </div>
        </Section>
      </Container>
    </section>
  );
}
